class NoxenHeroStack {
  constructor(root) {
    this.root = root;
    this.stage = root.querySelector('.a-noxen-hero-stack__stage');
    this.panels = Array.from(root.querySelectorAll('[data-hero-panel]'));
    this.number = root.querySelector('[data-stack-number]');
    this.progress = root.querySelector('[data-stack-progress]');

    this.positions = ['front', 'middle', 'back'];
    this.activeIndex = 0;

    this.interval = Number(root.dataset.interval) || 5200;
    this.autoplay = root.dataset.autoplay !== 'false';

    this.timer = null;
    this.isPaused = false;
    this.isAnimating = false;

    this.reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (!this.stage || this.panels.length < 2) return;

    this.init();
  }

  init() {
    this.root.style.setProperty(
      '--noxen-stack-interval',
      `${this.interval}ms`
    );

    const frontPanelIndex = this.panels.findIndex(
      panel => panel.dataset.position === 'front'
    );

    this.activeIndex = frontPanelIndex >= 0 ? frontPanelIndex : 0;

    this.updateState(false);
    this.bindEvents();

    if (this.autoplay && !this.reducedMotion) {
      this.start();
    }
  }

  bindEvents() {
    this.root.addEventListener('mouseenter', () => {
      this.pause();
    });

    this.root.addEventListener('mouseleave', () => {
      this.resetTilt();

      if (this.autoplay && !this.reducedMotion) {
        this.resume();
      }
    });

    this.panels.forEach((panel, index) => {
      panel.addEventListener('mouseenter', () => {
        if (panel.dataset.position !== 'front') {
          this.goTo(index);
        }
      });

      panel.addEventListener('focus', () => {
        this.pause();

        if (panel.dataset.position !== 'front') {
          this.goTo(index);
        }
      });

      panel.addEventListener('blur', () => {
        if (this.autoplay && !this.reducedMotion) {
          this.resume();
        }
      });
    });

    this.stage.addEventListener('mousemove', event => {
      if (this.reducedMotion) return;

      const rect = this.stage.getBoundingClientRect();
      const pointerX = (event.clientX - rect.left) / rect.width;
      const pointerY = (event.clientY - rect.top) / rect.height;

      const rotateY = (pointerX - 0.5) * 5;
      const rotateX = (0.5 - pointerY) * 4;

      this.stage.style.setProperty(
        '--noxen-tilt-x',
        `${rotateX.toFixed(2)}deg`
      );

      this.stage.style.setProperty(
        '--noxen-tilt-y',
        `${rotateY.toFixed(2)}deg`
      );
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stop();
      } else if (
        this.autoplay &&
        !this.isPaused &&
        !this.reducedMotion
      ) {
        this.start();
      }
    });

    /*
     * Optionale Verbindung zu den bereits vorhandenen unteren Karten.
     *
     * Ergänze dort lediglich:
     * data-noxen-world-trigger="digital"
     * data-noxen-world-trigger="streetwear"
     * data-noxen-world-trigger="workwear"
     *
     * Optisch wird an den Karten nichts verändert.
     */
    document
      .querySelectorAll('[data-noxen-world-trigger]')
      .forEach(trigger => {
        const world = trigger.dataset.noxenWorldTrigger;

        trigger.addEventListener('mouseenter', () => {
          const index = this.panels.findIndex(
            panel => panel.dataset.world === world
          );

          if (index >= 0) {
            this.pause();
            this.goTo(index);
          }
        });

        trigger.addEventListener('mouseleave', () => {
          if (this.autoplay && !this.reducedMotion) {
            this.resume();
          }
        });

        trigger.addEventListener('focus', () => {
          const index = this.panels.findIndex(
            panel => panel.dataset.world === world
          );

          if (index >= 0) {
            this.pause();
            this.goTo(index);
          }
        });

        trigger.addEventListener('blur', () => {
          if (this.autoplay && !this.reducedMotion) {
            this.resume();
          }
        });
      });
  }

  next() {
    const nextIndex = (this.activeIndex + 1) % this.panels.length;
    this.goTo(nextIndex);
  }

  goTo(targetIndex) {
  if (
    this.isAnimating ||
    targetIndex === this.activeIndex ||
    !this.panels[targetIndex]
  ) {
    return;
  }

  this.isAnimating = true;

  const oldActiveIndex = this.activeIndex;
  const oldActivePanel = this.panels[oldActiveIndex];
  const nextActivePanel = this.panels[targetIndex];

  oldActivePanel.classList.add('is-leaving');
  nextActivePanel.classList.add('is-preparing');

  window.setTimeout(() => {
    this.activeIndex = targetIndex;
    this.updateState(false);

    nextActivePanel.classList.remove('is-preparing');
    nextActivePanel.classList.add('is-entering');

    this.restartProgress();
  }, 280);

  window.setTimeout(() => {
    oldActivePanel.classList.remove('is-leaving');
    nextActivePanel.classList.remove('is-entering');
    this.isAnimating = false;
  }, 1450);
}

  updateState(animateReflection = true) {
    this.panels.forEach((panel, index) => {
      const relativeIndex =
        (index - this.activeIndex + this.panels.length) %
        this.panels.length;

      const position =
        this.positions[relativeIndex] || this.positions.at(-1);

      panel.dataset.position = position;
      panel.setAttribute(
        'aria-hidden',
        position === 'front' ? 'false' : 'true'
      );

      if (position === 'front') {
        panel.removeAttribute('tabindex');
      } else {
        panel.setAttribute('tabindex', '-1');
      }

      panel.classList.remove('is-entering');
    });

    const activePanel = this.panels[this.activeIndex];
    const activeWorld = activePanel.dataset.world || 'digital';

    this.root.dataset.activeWorld = activeWorld;

    if (this.number) {
      this.number.textContent = String(this.activeIndex + 1).padStart(2, '0');
    }

    if (animateReflection && activePanel) {
      requestAnimationFrame(() => {
        activePanel.classList.add('is-entering');

        window.setTimeout(() => {
          activePanel.classList.remove('is-entering');
        }, 1200);
      });
    }
  }

  start() {
    this.stop();

    if (!this.autoplay || this.reducedMotion || this.isPaused) return;

    this.root.classList.add('is-running');
    this.restartProgress();

    this.timer = window.setInterval(() => {
      this.next();
    }, this.interval);
  }

  stop() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    this.root.classList.remove('is-running');
  }

  pause() {
    this.isPaused = true;
    this.root.classList.add('is-paused');
    this.stop();
  }

  resume() {
    this.isPaused = false;
    this.root.classList.remove('is-paused');
    this.start();
  }

  restartProgress() {
    if (!this.progress || this.reducedMotion) return;

    this.root.classList.remove('is-running');
    this.progress.style.animation = 'none';

    void this.progress.offsetWidth;

    this.progress.style.animation = '';

    if (!this.isPaused && this.autoplay) {
      this.root.classList.add('is-running');
    }
  }

  resetTilt() {
    this.stage.style.setProperty('--noxen-tilt-x', '0deg');
    this.stage.style.setProperty('--noxen-tilt-y', '0deg');
  }

  destroy() {
    this.stop();
  }
}

function initNoxenHeroStacks(scope = document) {
  scope.querySelectorAll('[data-noxen-hero-stack]').forEach(root => {
    if (root.dataset.noxenStackInitialized === 'true') return;

    root.dataset.noxenStackInitialized = 'true';
    root.noxenHeroStack = new NoxenHeroStack(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initNoxenHeroStacks();
});

/*
 * Shopify Theme Editor:
 * Section nach Änderungen erneut initialisieren.
 */
document.addEventListener('shopify:section:load', event => {
  initNoxenHeroStacks(event.target);
});
class NoxenUniverse {
  constructor(root) {
    this.root = root;
    this.layers = Array.from(
      root.querySelectorAll('[data-universe-depth]')
    );

    this.reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    this.pointer = {
      currentX: 0,
      currentY: 0,
      targetX: 0,
      targetY: 0
    };

    this.frame = null;
    this.bounds = null;

    if (this.reducedMotion) return;

    this.init();
  }

  init() {
    this.bindEvents();
    this.updateBounds();
    this.animate();
  }

  bindEvents() {
    this.root.addEventListener(
      'pointerenter',
      () => {
        this.updateBounds();
      },
      { passive: true }
    );

    this.root.addEventListener(
      'pointermove',
      event => {
        this.handlePointerMove(event);
      },
      { passive: true }
    );

    this.root.addEventListener(
      'pointerleave',
      () => {
        this.pointer.targetX = 0;
        this.pointer.targetY = 0;
      },
      { passive: true }
    );

    window.addEventListener(
      'resize',
      () => {
        this.updateBounds();
      },
      { passive: true }
    );
  }

  updateBounds() {
    this.bounds = this.root.getBoundingClientRect();
  }

  handlePointerMove(event) {
    if (!this.bounds) {
      this.updateBounds();
    }

    const relativeX =
      (event.clientX - this.bounds.left) / this.bounds.width;

    const relativeY =
      (event.clientY - this.bounds.top) / this.bounds.height;

    this.pointer.targetX = (relativeX - 0.5) * 34;
    this.pointer.targetY = (relativeY - 0.5) * 26;
  }

  animate() {
    const easing = 0.055;

    this.pointer.currentX +=
      (this.pointer.targetX - this.pointer.currentX) * easing;

    this.pointer.currentY +=
      (this.pointer.targetY - this.pointer.currentY) * easing;

    this.root.style.setProperty(
      '--noxen-universe-x',
      `${this.pointer.currentX.toFixed(2)}px`
    );

    this.root.style.setProperty(
      '--noxen-universe-y',
      `${this.pointer.currentY.toFixed(2)}px`
    );

    this.layers.forEach(layer => {
      const depth = Number(layer.dataset.universeDepth) || 0;

      const moveX = this.pointer.currentX * depth;
      const moveY = this.pointer.currentY * depth;

      layer.style.setProperty(
        '--universe-layer-x',
        `${moveX.toFixed(2)}px`
      );

      layer.style.setProperty(
        '--universe-layer-y',
        `${moveY.toFixed(2)}px`
      );
    });

    this.frame = window.requestAnimationFrame(() => {
      this.animate();
    });
  }

  destroy() {
    if (this.frame) {
      window.cancelAnimationFrame(this.frame);
    }
  }
}

function initNoxenUniverses(scope = document) {
  scope.querySelectorAll('[data-noxen-universe]').forEach(root => {
    if (root.dataset.noxenUniverseInitialized === 'true') {
      return;
    }

    root.dataset.noxenUniverseInitialized = 'true';
    root.noxenUniverse = new NoxenUniverse(root);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initNoxenUniverses();
});

document.addEventListener('shopify:section:load', event => {
  initNoxenUniverses(event.target);
});